/**
 * avatarRewards — skiny-nagrody za „Pełny album: <biom>" po stronie klienta
 * (DESIGN_ALBUM_BIOME_SKINS.md).
 *
 * Źródłem prawdy o tym, KTÓRE skiny daje dane osiągnięcie, jest serwer
 * (`rewardSkinIds` w GET /achievements/me). Klient dokłada tylko bramkę
 * grafiki: skin w stagingu (`enabled:false` w avatars.config) nie jest
 * pokazywany, więc karta nagrody czeka, aż grafiki trafią do gry.
 *
 * Wspólne dla: AchievementsModal (karty + odbiór), BankAlbumModal (baner
 * „Pełny album!" po wklejeniu ostatniej naklejki biomu), AvatarCustomizeModal
 * (zakładanie skina), main.tsx (kropka „nowa nagroda" przy starcie).
 */

import { achievementsApi, type AchievementView } from '@/net/api';
import { AVATAR_SKINS_BY_ID, isAvatarSkinEnabled } from '@/config/avatars.config';
import { saveManager } from '@/save';
import { useGameStore } from '@/ui/store/gameStore';
import { eventBus } from '@/shared/EventBus';
import type { AvatarSkinDef, AvatarSkinId } from '@/domain/types/avatar';

/** Osiągnięcie z nagrodą-skinem („Pełny album"). Brak `kind` = starsze API → kamień milowy. */
export function isAlbumReward(a: AchievementView): boolean {
  return a.kind === 'album_biome';
}

/** Skiny-nagrody osiągnięcia, które klient zna i które mają już grafikę. */
export function rewardSkinsOf(a: AchievementView): AvatarSkinDef[] {
  return (a.rewardSkinIds ?? [])
    .map((id) => AVATAR_SKINS_BY_ID[id])
    .filter((s): s is AvatarSkinDef => s !== undefined && isAvatarSkinEnabled(s));
}

/** Kamień milowy — zawsze; „Pełny album" — dopiero gdy jego skiny wyszły ze stagingu. */
export function isAchievementVisible(a: AchievementView): boolean {
  return !isAlbumReward(a) || rewardSkinsOf(a).length > 0;
}

/** Id nagród gotowych do odebrania (tylko widoczne — staging nie zapala kropki). */
export function claimableRewardIds(items: readonly AchievementView[]): string[] {
  return items
    .filter((a) => isAlbumReward(a) && a.claimable === true && isAchievementVisible(a))
    .map((a) => a.id);
}

/**
 * Pobiera GET /achievements/me i odświeża kropkę „nowa nagroda" w menu.
 * Fire-and-forget: błąd sieci zostawia poprzedni stan (zwraca `null`).
 */
export async function refreshClaimableAchievements(): Promise<AchievementView[] | null> {
  try {
    const res = await achievementsApi.me();
    useGameStore.getState().setClaimableAchievementIds(claimableRewardIds(res.achievements));
    return res.achievements;
  } catch (err) {
    console.warn('[avatarRewards] odświeżenie osiągnięć nieudane (non-blocking):', err);
    return null;
  }
}

/**
 * Po skompletowaniu biomu w albumie: czy czeka na niego nagroda do odebrania?
 * Pyta serwer (nie zgaduje) — biom, który dostał nowe pety PO odebraniu
 * nagrody, znów się skompletuje, ale nagrody drugi raz już nie ma.
 */
export async function findClaimableBiomeReward(biomeId: string): Promise<AchievementView | null> {
  const items = await refreshClaimableAchievements();
  return (
    items?.find(
      (a) => isAlbumReward(a) && a.biomeId === biomeId && a.claimable === true && isAchievementVisible(a),
    ) ?? null
  );
}

/**
 * Zakłada skin (musi być odblokowany — serwer i tak waliduje go w
 * `sanitizeProfile`). Zapis przez save (profile jest zapisywalny), store
 * i live-swap sprite'a w aktywnej scenie (`Player.setSkin`).
 */
export async function equipAvatarSkin(skinId: AvatarSkinId): Promise<void> {
  await saveManager.update((current) => ({
    ...current,
    profile: { ...current.profile, avatar: { ...current.profile.avatar, skinId } },
  }));
  useGameStore.getState().setAvatarSkinId(skinId);
  eventBus.emit('avatar:changed', { skinId });
}
